import { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Loader2, Search, AlertTriangle, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

const RESTAURANT_ICON = new L.DivIcon({
  className: "",
  html: `<div style="background:#D4B483;color:#0A0A0A;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,0.3)">🏪</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const PIN_ICON = new L.DivIcon({
  className: "",
  html: `<div style="background:#EF4444;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,0.3);border:2px solid white">📍</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MarkerUpdater({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 14));
  }, [position, map]);
  return null;
}

export default function MapAddressPicker({ restaurantLat, restaurantLng, restaurantAddress, onAddressChange, initialAddress }) {
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [marker, setMarker] = useState(null);
  const [resolvedDisplay, setResolvedDisplay] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [deliveryCheck, setDeliveryCheck] = useState(null);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const debounceRef = useRef(null);
  const initialAddressRef = useRef(null);
  const forwardGeocodeRef = useRef(null);

  const centerLat = restaurantLat || 56.277;
  const centerLng = restaurantLng || 14.537;

  const emitAddress = useCallback((st, ci, lat, lng, display) => {
    onAddressChange({ street: st, city: ci, display: display || `${st}, ${ci}`, lat, lng });
  }, [onAddressChange]);

  const checkDelivery = useCallback(async (st, ci) => {
    if (!st && !ci) { setDeliveryCheck(null); return; }
    setCheckingDelivery(true);
    try {
      const res = await api.post("/orders/check-delivery-address", { street: st, city: ci });
      setDeliveryCheck(res.data);
    } catch {
      setDeliveryCheck(null);
    } finally {
      setCheckingDelivery(false);
    }
  }, []);

  const reverseGeocode = useCallback(async (lat, lng) => {
    setGeocoding(true);
    setDeliveryCheck(null);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&accept-language=sv`,
        { headers: { "User-Agent": "biten-app/1.0" } }
      );
      const data = await res.json();
      const addr = data.address || {};
      const st = addr.road || addr.pedestrian || addr.street || "";
      const ci = addr.city || addr.town || addr.village || addr.municipality || "";
      const display = [st, ci].filter(Boolean).join(", ") || data.display_name || "";
      setStreet(st);
      setCity(ci);
      setResolvedDisplay(display);
      emitAddress(st, ci, lat, lng, display);
      if (st || ci) checkDelivery(st, ci);
    } catch {
      const display = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      setStreet("");
      setCity("");
      setResolvedDisplay(display);
      emitAddress("", "", lat, lng, display);
    } finally {
      setGeocoding(false);
    }
  }, [emitAddress, checkDelivery]);

  const forwardGeocode = useCallback(async (st, ci) => {
    const query = [st, ci].filter(Boolean).join(", ");
    if (!query.trim()) return;
    setGeocoding(true);
    setDeliveryCheck(null);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=jsonv2&limit=1&countrycodes=se`,
        { headers: { "User-Agent": "biten-app/1.0" } }
      );
      const data = await res.json();
      if (data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        const display = data[0].display_name || "";
        setMarker([lat, lng]);
        setResolvedDisplay(display);
        emitAddress(st, ci, lat, lng, display);
        checkDelivery(st, ci);
      } else {
        setResolvedDisplay("Hittade inte adressen – klicka på kartan");
      }
    } catch {
      setResolvedDisplay("Kunde inte söka – klicka på kartan");
    } finally {
      setGeocoding(false);
    }
  }, [emitAddress, checkDelivery]);

  forwardGeocodeRef.current = forwardGeocode;

  useEffect(() => {
    if (!initialAddress) return;
    if (initialAddressRef.current === initialAddress) return;
    initialAddressRef.current = initialAddress;
    const st = initialAddress.street || "";
    const ci = initialAddress.city || "";
    setStreet(st);
    setCity(ci);
    setMarker(null);
    setResolvedDisplay("");
    setDeliveryCheck(null);
    if ((st.trim() || ci.trim()) && forwardGeocodeRef.current) {
      forwardGeocodeRef.current(st, ci);
    }
  }, [initialAddress]);

  const handleStreetChange = (val) => {
    setStreet(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim() && city.trim()) forwardGeocode(val, city);
    }, 800);
  };

  const handleCityChange = (val) => {
    setCity(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (street.trim() && val.trim()) forwardGeocode(street, val);
    }, 800);
  };

  const handlePick = (lat, lng) => {
    setMarker([lat, lng]);
    reverseGeocode(lat, lng);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[#D4D4D8] text-xs mb-1">{restaurantAddress ? "Gatuadress" : "Street"}</Label>
          <Input value={street} onChange={e => handleStreetChange(e.target.value)}
            placeholder="Storgatan 42" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" />
        </div>
        <div>
          <Label className="text-[#D4D4D8] text-xs mb-1">{restaurantAddress ? "Stad" : "City"}</Label>
          <Input value={city} onChange={e => handleCityChange(e.target.value)}
            placeholder="Olofström" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" />
        </div>
      </div>

      <div className="rounded-xl overflow-hidden border border-[#27272A]" style={{ height: 260 }}>
        <MapContainer center={[centerLat, centerLng]} zoom={14} style={{ height: "100%", width: "100%" }} zoomControl={false}>
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <ClickHandler onPick={handlePick} />
          {restaurantLat && restaurantLng && (
            <Marker position={[restaurantLat, restaurantLng]} icon={RESTAURANT_ICON} />
          )}
          {marker && (
            <>
              <Marker position={marker} icon={PIN_ICON} />
              <MarkerUpdater position={marker} />
            </>
          )}
        </MapContainer>
      </div>

      {geocoding && (
        <div className="flex items-center gap-2 text-[#D4B483] text-xs">
          <Loader2 className="w-3 h-3 animate-spin" />
          Söker adress...
        </div>
      )}

      {marker && resolvedDisplay && !geocoding && (
        <div className="bg-[#121212] rounded-xl p-4 border border-[#27272A] flex items-start gap-3">
          <div className="mt-0.5">
            <MapPin className="w-5 h-5 text-[#10B981]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#EDEDED] text-sm">{resolvedDisplay}</p>
            <p className="text-[#D4D4D8] text-xs mt-0.5">Klicka på kartan för att justera</p>
          </div>
        </div>
      )}

      {checkingDelivery && (
        <div className="flex items-center gap-2 text-[#D4D4D8] text-xs">
          <Loader2 className="w-3 h-3 animate-spin" />
          Kontrollerar leveransavstånd...
        </div>
      )}

      {deliveryCheck && !checkingDelivery && (
        <div className={`rounded-xl p-4 border flex items-start gap-3 ${deliveryCheck.allowed ? "bg-[#10B981]/10 border-[#10B981]/40" : "bg-[#EF4444]/10 border-[#EF4444]/40"}`}>
          <div className="mt-0.5">
            {deliveryCheck.allowed
              ? <CheckCircle className="w-5 h-5 text-[#10B981]" />
              : <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            }
          </div>
          <div className="flex-1 min-w-0">
            {deliveryCheck.allowed ? (
              <p className="text-[#EDEDED] text-sm">
                {deliveryCheck.estimated_minutes
                  ? `✅ Leverans möjlig – ${deliveryCheck.estimated_minutes.toFixed(0)} min från Bästa Biten`
                  : "✅ Leverans möjlig"
                }
              </p>
            ) : (
              <div>
                <p className="text-[#FCA5A5] text-sm font-medium">
                  ❌ {deliveryCheck.estimated_minutes
                    ? `Adressen är ${deliveryCheck.estimated_minutes.toFixed(0)} min från Bästa Biten`
                    : "Leverans ej möjlig till denna adress"
                  }
                </p>
                <p className="text-[#FCA5A5] text-xs mt-0.5">
                  Max {deliveryCheck.max_minutes.toFixed(0)} min med bil
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {!marker && !geocoding && (
        <p className="text-[#D4D4D8] text-xs text-center">Skriv din adress ovan eller klicka direkt på kartan</p>
      )}
    </div>
  );
}
